"use client";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import api from "../../api/axios";
import ENDPOINTS from "../../api/endpoints";

export default function useDoctorDetailsPage() {
  const { t } = useTranslation();
  const { doctorId } = useParams();

  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(Boolean(doctorId));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!doctorId) {
        setDoctor(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      // Validate ID format (expecting UUID). If invalid, avoid calling backend.
      const isUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(s || ""));
      if (!isUuid(doctorId)) {
        if (!cancelled) {
          setError(t("doctors.invalidId") || "Invalid doctor identifier.");
          setLoading(false);
        }
        return;
      }

      try {
        // GET /api/doctors/:id  (via gateway)
        const url = ENDPOINTS.REGISTER_DOCTOR.BY_ID(doctorId);
        const { data } = await api.get(url);
        const doctorData = data?.data || data?.doctor || data;
        if (!cancelled) {
          setDoctor(
            doctorData
              ? { ...doctorData, id: doctorData.id || doctorData._id || doctorId }
              : null,
          );
        }
      } catch (e) {
        console.error('[useDoctorDetailsPage] Error fetching doctor:', e);
        if (!cancelled)
          setError(
            e?.response?.data?.message || e?.response?.data?.error || e?.message || "Erreur chargement médecin"
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [doctorId]);

  const notFound = useMemo(
    () => !loading && !error && !doctor && Boolean(doctorId),
    [loading, error, doctor, doctorId]
  );

  return { doctor, loading, error, notFound };
}
